/**
 * What this system records about its own behaviour.
 *
 * TWO QUESTIONS, ONE PIPE. "Is anybody using this?" and "what is broken?" are
 * answered from the same event stream because they are the same events seen
 * from different ends — a route that errors half the time is both a usage
 * fact and a defect. Splitting them into two systems is how you end up with
 * a dashboard that says everything is fine beside a log full of stack traces.
 *
 * THE RULES THIS FILE EXISTS TO FIX IN PLACE, because every one of them is
 * easy to break later and expensive to notice:
 *
 *   NOTHING A REQUESTER TYPED. No subjects, no bodies, no email addresses,
 *   no custom field values. A ticket subject in a telemetry row is a ticket
 *   subject in a dashboard that a platform admin of ANOTHER desk can read,
 *   and the whole tenancy boundary is built to prevent exactly that. Route
 *   TEMPLATES, not paths: `/api/v1/tenants/:tenant/tickets/:number`, never
 *   the filled-in one.
 *
 *   NEVER IN THE REQUEST PATH. Telemetry that can slow a page or fail a save
 *   is worse than no telemetry. Every producer buffers and flushes out of
 *   band, and a failed flush is dropped, not retried into a queue that grows
 *   until something falls over.
 *
 *   INVISIBLE TO THE PERSON WORKING. No banners, no prompts, no consent
 *   dialog for a desk tool recording its own error rate.
 *
 * `fingerprint` is the part that makes errors actionable rather than merely
 * collected: 400 rows of the same crash are one problem, and a dashboard
 * that cannot say so is a log file with worse ergonomics.
 */

/** Which part of the system produced the event. */
export const TELEMETRY_SOURCES = ['web', 'api', 'worker'] as const;
export type TelemetrySource = (typeof TELEMETRY_SOURCES)[number];

export const TELEMETRY_KINDS = [
  /** A screen was opened. Usage, not navigation history. */
  'page',
  /** An HTTP request completed, whatever its status. */
  'request',
  /** A named thing a person chose to do — resolving, linking, parking. */
  'action',
  /** Something threw, anywhere. */
  'error',
  /** A background loop finished a pass. */
  'job',
] as const;
export type TelemetryKind = (typeof TELEMETRY_KINDS)[number];

/**
 * One recorded event.
 *
 * Deliberately flat and small. A nested free-form blob is how ticket text
 * eventually arrives in here by accident — `meta` is bounded and every
 * producer is expected to put only values it would be happy to show another
 * tenant's administrator.
 */
export interface TelemetryEvent {
  source: TelemetrySource;
  kind: TelemetryKind;
  /**
   * What happened, as a stable identifier.
   *
   * For `request`, the route TEMPLATE. For `page`, the route pattern. For
   * `action`, a verb like `ticket.resolve`. Stability matters more than
   * precision: a name that changes per ticket cannot be counted.
   */
  name: string;
  /** Milliseconds, where the event has a duration. */
  durationMs?: number;
  /**
   * HTTP status for a request.
   *
   * For a `job` it is the number of FAILURES in that pass, not a 0/1 exit
   * code: these loops process many items per pass, and "the sweep finished
   * but three tenants failed" is the fact worth keeping. Zero means a clean
   * pass, which is also what makes `status > 0` the failure filter.
   */
  status?: number;
  /** Which desk, when the event happened inside one. */
  tenantId?: string | null;
  /** Who, as an oid. Never an email — this is for counting, not contacting. */
  userOid?: string | null;
  /** Set on `error`. The grouping key; see `fingerprint`. */
  errorFingerprint?: string | null;
  /** The message, already scrubbed. Bounded hard at 500 characters. */
  errorMessage?: string | null;
  /** First few frames, scrubbed of absolute paths. */
  errorStack?: string | null;
  /** Build identity, so a spike can be tied to a deploy. */
  release?: string | null;
  /** Small, bounded, and never free text a person typed. */
  meta?: Record<string, string | number | boolean | null>;
  /** When it happened, ISO. The producer's clock; the server records its own too. */
  at?: string;
}

/** The body a producer POSTs. Batched, because one request per event is not free. */
export interface TelemetryBatch {
  release?: string | null;
  events: TelemetryEvent[];
}

/** Hard bounds, enforced on both sides so neither has to trust the other. */
export const TELEMETRY_LIMITS = {
  /** Per POST. A client with more than this is misbehaving or looping. */
  maxEventsPerBatch: 100,
  maxNameLength: 120,
  maxMessageLength: 500,
  maxStackLength: 2000,
  maxMetaKeys: 12,
  maxMetaValueLength: 200,
} as const;

/**
 * Collapse an error to a stable grouping key.
 *
 * THE POINT: four hundred rows of the same crash are one problem. Grouping is
 * what turns a pile of events into a list somebody can work through, and it
 * has to survive the things that differ between two occurrences of the same
 * bug — ids, numbers, paths, ports, quoted values.
 *
 * Normalisation is deliberately aggressive. Over-grouping shows one issue
 * where there were two, which a human notices reading the samples;
 * under-grouping shows four hundred issues where there was one, which nobody
 * reads at all. The first failure is recoverable and the second is not.
 */
export function fingerprint(message: string, stack?: string | null): string {
  const normalised = message
    .toLowerCase()
    // uuids, then long hex, then any run of digits: ids and ticket numbers.
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '<id>')
    .replace(/\b[0-9a-f]{16,}\b/g, '<hex>')
    .replace(/\d+/g, '<n>')
    // Quoted fragments are usually the varying part: a key, a value, a path.
    .replace(/"[^"]*"/g, '<q>')
    .replace(/'[^']*'/g, '<q>')
    .replace(/\s+/g, ' ')
    .trim();

  // The top frame that belongs to us. A stack's first line is often framework
  // code shared by unrelated failures, so grouping on it merges real
  // differences; our own frame is the one that identifies the bug.
  const frame =
    (stack ?? '')
      .split('\n')
      .map((l) => l.trim())
      .find((l) => /packages[\\/]/.test(l) && !/node_modules/.test(l)) ?? '';
  const frameKey = frame
    .replace(/:\d+:\d+/g, '')
    .replace(/.*[\\/]packages[\\/]/, '')
    .slice(0, 120);

  return djb2(`${normalised}|${frameKey}`);
}

/**
 * A short, stable hash.
 *
 * Not cryptographic and does not need to be: this groups errors, it does not
 * protect anything. Written out rather than imported so the browser bundle
 * does not pull in node crypto for eight lines.
 */
function djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Strip the things that must never be stored, from text that came from an
 * exception.
 *
 * Errors quote their inputs. A failed query prints its parameters, a failed
 * fetch prints its URL, and a validation error prints the value that failed —
 * which on this system is somebody's ticket subject or email address. This is
 * the last gate before that reaches a shared dashboard.
 */
// `max: number`, NOT inferred from the default. `TELEMETRY_LIMITS` is `as
// const`, so an inferred parameter type would be the literal `500` and every
// caller scrubbing a stack (2000) would fail to compile.
export function scrubText(text: string, max: number = TELEMETRY_LIMITS.maxMessageLength): string {
  return text
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '<email>')
    // Anything that looks like a bearer, key or password, with its value.
    .replace(/(bearer|token|secret|password|key)\s*[=:]\s*\S+/gi, '$1=<redacted>')
    .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '<jwt>')
    // Absolute paths differ per machine and per container; they are noise
    // that also happens to leak the filesystem layout.
    .replace(/[A-Za-z]:\\[^\s"']+/g, '<path>')
    .replace(/\/(?:home|Users|app|usr)\/[^\s"']+/g, '<path>')
    .slice(0, max);
}

/**
 * Turn a filled-in path back into the template it was routed by.
 *
 * `/api/v1/tenants/esatech/tickets/120198` counted as its own route gives one
 * row per ticket and answers nothing. Used by the web client, which does not
 * get the matched route for free the way Fastify does.
 */
export function routeTemplate(path: string): string {
  return path
    .split('?')[0]!
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/g, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:n')
    .slice(0, TELEMETRY_LIMITS.maxNameLength);
}
